import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon, LatLngExpression } from 'leaflet';
import { useAuth } from '../contexts/AuthContext';
import { getSnares, getCarcasses } from '../lib/db';
import 'leaflet/dist/leaflet.css';

// Fix for default markers not appearing
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface Snare {
  id: string;
  snareNumber: string;
  latitude: number;
  longitude: number;
  material: string;
  description?: string;
  loggedAt: string;
  createdAt: string;
}

interface Carcass {
  id: string;
  carcassNumber: string;
  latitude: number;
  longitude: number;
  species?: string;
  hadSnare: boolean;
  description?: string;
  loggedAt: string;
  createdAt: string;
}

// Custom icon for carcasses
const carcassIcon = new Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path fill="#dc2626" stroke="#fff" stroke-width="2" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5s12.5-20.1 12.5-28.5C25 5.6 19.4 0 12.5 0z"/>
      <circle cx="12.5" cy="12.5" r="5" fill="#fff"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Custom icon for snares
const snareIcon = new Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path fill="#2563eb" stroke="#fff" stroke-width="2" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5s12.5-20.1 12.5-28.5C25 5.6 19.4 0 12.5 0z"/>
      <circle cx="12.5" cy="12.5" r="5" fill="#fff"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Component to fit bounds when markers change
function FitBounds({ positions }: { positions: LatLngExpression[] }) {
  const map = useMap();

  useEffect(() => {
    if (positions.length > 0) {
      const bounds = positions.map(p => p as [number, number]);
      if (bounds.length === 1) {
        map.setView(bounds[0], 13);
      } else {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [positions, map]);

  return null;
}

interface MapProps {
  selectedRecordId?: string | null;
  selectedRecordType?: 'snare' | 'carcass' | null;
}

export function Map({ selectedRecordId, selectedRecordType }: MapProps = {}) {
  const { user } = useAuth();
  const [snares, setSnares] = useState<Snare[]>([]);
  const [carcasses, setCarcasses] = useState<Carcass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSnares, setShowSnares] = useState(true);
  const [showCarcasses, setShowCarcasses] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [snaresRes, carcassesRes] = await Promise.all([
        fetch('/api/snares', { headers }),
        fetch('/api/carcasses', { headers })
      ]);

      if (!snaresRes.ok || !carcassesRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const snaresData = await snaresRes.json();
      const carcassesData = await carcassesRes.json();

      setSnares(snaresData.snares || []);
      setCarcasses(carcassesData.carcasses || []);
    } catch (err) {
      // If API fails, try to load from local storage
      try {
        const [localSnares, localCarcasses] = await Promise.all([
          getSnares(),
          getCarcasses()
        ]);
        setSnares(localSnares as any);
        setCarcasses(localCarcasses as any);
        setError(null); // Clear error since we have local data
      } catch (localErr) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }

  const defaultCenter: LatLngExpression = [-1.286389, 36.817223]; // Nairobi, Kenya

  // If a record is selected, center on it
  const selectedRecord = selectedRecordId
    ? (selectedRecordType === 'snare'
        ? snares.find(s => s.id === selectedRecordId)
        : carcasses.find(c => c.id === selectedRecordId))
    : null;

  const centerPosition: LatLngExpression = selectedRecord
    ? [selectedRecord.latitude, selectedRecord.longitude]
    : defaultCenter;

  const allPositions: LatLngExpression[] = [
    ...(showSnares ? snares.map(s => [s.latitude, s.longitude] as LatLngExpression) : []),
    ...(showCarcasses ? carcasses.map(c => [c.latitude, c.longitude] as LatLngExpression) : [])
  ];

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Map</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Visualize snare and carcass locations
            </p>
          </div>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={showSnares}
                onChange={(e) => setShowSnares(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-600"></span>
                Snares ({snares.length})
              </span>
            </label>
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={showCarcasses}
                onChange={(e) => setShowCarcasses(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-600"></span>
                Carcasses ({carcasses.length})
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
            <p className="text-muted-foreground">Loading map data...</p>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
            <div className="p-4 bg-destructive/10 border border-destructive rounded-lg text-destructive max-w-md">
              {error}
            </div>
          </div>
        )}

        {!loading && !error && (
          <MapContainer
            center={selectedRecord ? centerPosition : defaultCenter}
            zoom={selectedRecord ? 15 : 6}
            className="h-full w-full"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {!selectedRecord && allPositions.length > 0 && <FitBounds positions={allPositions} />}

            {showSnares && snares.map((snare) => (
              <Marker
                key={snare.id}
                position={[snare.latitude, snare.longitude]}
                icon={snareIcon}
                eventHandlers={snare.id === selectedRecordId ? {
                  add: (e) => {
                    e.target.openPopup();
                  }
                } : undefined}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <h3 className="font-bold text-blue-600">{snare.snareNumber}</h3>
                    <div className="mt-2 space-y-1 text-sm">
                      <p><strong>Material:</strong> {snare.material}</p>
                      <p><strong>Coordinates:</strong> {snare.latitude.toFixed(6)}, {snare.longitude.toFixed(6)}</p>
                      {snare.description && (
                        <p><strong>Description:</strong> {snare.description}</p>
                      )}
                      <p><strong>Date:</strong> {new Date(snare.loggedAt || snare.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {showCarcasses && carcasses.map((carcass) => (
              <Marker
                key={carcass.id}
                position={[carcass.latitude, carcass.longitude]}
                icon={carcassIcon}
                eventHandlers={carcass.id === selectedRecordId ? {
                  add: (e) => {
                    e.target.openPopup();
                  }
                } : undefined}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <h3 className="font-bold text-red-600">{carcass.carcassNumber}</h3>
                    <div className="mt-2 space-y-1 text-sm">
                      {carcass.species && <p><strong>Species:</strong> {carcass.species}</p>}
                      <p><strong>Snare Involved:</strong> {carcass.hadSnare ? 'Yes' : 'No'}</p>
                      <p><strong>Coordinates:</strong> {carcass.latitude.toFixed(6)}, {carcass.longitude.toFixed(6)}</p>
                      {carcass.description && (
                        <p><strong>Description:</strong> {carcass.description}</p>
                      )}
                      <p><strong>Date:</strong> {new Date(carcass.loggedAt || carcass.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}

        {!loading && !error && snares.length === 0 && carcasses.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center max-w-md px-4 bg-white/90 p-6 rounded-lg">
              <p className="text-muted-foreground">No data to display on map yet</p>
              <p className="text-sm text-muted-foreground mt-2">
                Snares and carcasses will appear here once logged
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
